/**
 * One person's whole training picture, for their employee record — TRUS-562.
 *
 * Abby: "a manager needs that per person view bc its their employee — they should
 * see everything for that employee record." Every training the person has been
 * assigned (when it is due, why, and where it stands) and what they have completed.
 *
 * Shown on the Staff profile and when a manager opens someone in My Team. The server
 * decides who may read it: an admin, the person, or anyone above them in the
 * reporting line. A manager without `training.view_team_records` still sees the
 * assignments; the records section says what to ask for instead.
 *
 * Admins and the person's managers can also unassign a training here (TRUS-562 item
 * 6), with an optional reason; the server says whether the caller may (`can_unassign`).
 */

import React, { useCallback, useEffect, useState } from 'react'
import { Button, INPUT, Pill } from './ui'
import {
  Assignment,
  PersonTraining,
  formatDate,
  isDoneUntilNextDue,
  personTraining,
  statusTone,
  unassignTraining,
} from './api'

const STATUS_LABEL: Record<Assignment['status'], string> = {
  overdue: 'Overdue',
  due_soon: 'Due soon',
  pending: 'Scheduled',
  completed: 'Completed',
  cancelled: 'Cancelled',
}

const TH = 'py-1.5 pr-3 text-left font-medium text-ink-500'
const TD = 'py-1.5 pr-3 align-top'

export default function PersonTrainingPanel({ userId }: { userId: number }) {
  const [data, setData] = useState<PersonTraining | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Bumped after an unassign so the panel reloads.
  const [reloadKey, setReloadKey] = useState(0)
  const [confirming, setConfirming] = useState<{ id: number; reason: string; busy: boolean; error: string | null } | null>(
    null,
  )

  useEffect(() => {
    let cancelled = false
    setData(null)
    setError(null)
    personTraining(userId)
      .then((d) => {
        if (!cancelled) setData(d)
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load training.')
      })
    return () => {
      cancelled = true
    }
  }, [userId, reloadKey])

  const doUnassign = useCallback(async () => {
    if (!confirming) return
    setConfirming({ ...confirming, busy: true, error: null })
    try {
      await unassignTraining(confirming.id, confirming.reason)
      setConfirming(null)
      setReloadKey((k) => k + 1)
    } catch (e) {
      setConfirming({ ...confirming, busy: false, error: e instanceof Error ? e.message : 'Could not unassign.' })
    }
  }, [confirming])

  if (error) {
    return <p className="text-[13px] text-ink-500">Training isn&apos;t available for this person.</p>
  }
  if (!data) {
    return <p className="text-[13px] text-ink-500">Loading training…</p>
  }

  // Defensive about shape: an older backend (before TRUS-562) answers this path with
  // something else, and the section should then say nothing is assigned, not crash.
  const assignments = Array.isArray(data.assignments) ? data.assignments : []
  const counts = data.counts ?? {}
  const records = Array.isArray(data.records) ? data.records : null
  const records_restricted = Boolean(data.records_restricted)
  const canUnassign = Boolean(data.can_unassign)

  return (
    <div className="space-y-4 text-[13px]">
      <div className="flex flex-wrap gap-2">
        {counts.overdue ? <Pill tone="danger">{counts.overdue} overdue</Pill> : null}
        {counts.due_soon ? <Pill tone="attention">{counts.due_soon} due soon</Pill> : null}
        {counts.pending ? <Pill>{counts.pending} scheduled</Pill> : null}
        {counts.completed ? <Pill tone="success">{counts.completed} completed</Pill> : null}
        {assignments.length === 0 ? <Pill tone="attention">No training assigned</Pill> : null}
      </div>

      <section>
        <h4 className="mb-1 font-mono text-[10px] uppercase tracking-[0.14em] text-ink-500">Assigned trainings</h4>
        {assignments.length === 0 ? (
          <p className="text-ink-500">Nothing is assigned to this person.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className={TH}>Training</th>
                  <th className={TH}>Status</th>
                  <th className={TH}>Due</th>
                  <th className={TH}>Completed</th>
                  {canUnassign && <th className={TH} />}
                </tr>
              </thead>
              <tbody>
                {assignments.map((a) => (
                  <React.Fragment key={a.id}>
                    <tr className="border-t border-v2border-muted">
                      <td className={`${TD} text-ink`}>
                        {a.training_title}
                        {a.is_mandatory ? <span className="ml-1 text-[11px] text-ink-400">(required)</span> : null}
                      </td>
                      <td className={TD}>
                        {/* TRUS-562: a repeating training done for this cycle reads as completed. */}
                        {isDoneUntilNextDue(a) ? (
                          <Pill tone="success">Completed · next due {a.due_date ? formatDate(a.due_date) : 'later'}</Pill>
                        ) : (
                          <Pill tone={statusTone(a.status)}>{STATUS_LABEL[a.status]}</Pill>
                        )}
                      </td>
                      <td className={TD}>
                        {a.due_date ? formatDate(a.due_date) : <span className="text-ink-400">No due date</span>}
                      </td>
                      <td className={TD}>
                        {a.completed_date ? formatDate(a.completed_date) : <span className="text-ink-400">—</span>}
                      </td>
                      {canUnassign && (
                        <td className={`${TD} text-right`}>
                          {confirming?.id !== a.id && (
                            <Button
                              variant="ghost"
                              onClick={() => setConfirming({ id: a.id, reason: '', busy: false, error: null })}
                              title="Take this training off this person"
                            >
                              Unassign
                            </Button>
                          )}
                        </td>
                      )}
                    </tr>
                    {confirming?.id === a.id && (
                      <tr>
                        <td colSpan={5} className="pb-3">
                          <div className="rounded-md border border-attention bg-attention-soft p-3 text-attention-ink">
                            <p>
                              Unassign {a.training_title}? Completion records are kept, and it won&apos;t be assigned
                              again automatically.
                            </p>
                            {confirming.error && <p className="mt-1 text-danger">{confirming.error}</p>}
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                              <input
                                className={`${INPUT} min-w-[200px] flex-1`}
                                placeholder="Reason (optional)"
                                value={confirming.reason}
                                onChange={(e) => setConfirming({ ...confirming, reason: e.target.value })}
                                aria-label="Reason for unassigning"
                              />
                              <Button onClick={() => setConfirming(null)} disabled={confirming.busy}>
                                Keep
                              </Button>
                              <Button variant="danger" onClick={() => void doUnassign()} disabled={confirming.busy}>
                                {confirming.busy ? 'Unassigning…' : 'Unassign'}
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <h4 className="mb-1 font-mono text-[10px] uppercase tracking-[0.14em] text-ink-500">Completion records</h4>
        {records_restricted || records === null ? (
          <p className="text-ink-500">
            Your role hasn&apos;t been given access to team training records yet. An administrator can turn on{' '}
            <strong>View team training records</strong> for your role under Roles → Functions.
          </p>
        ) : records.length === 0 ? (
          <p className="text-ink-500">No completed training on file.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className={TH}>Training</th>
                  <th className={TH}>Completed</th>
                  <th className={TH}>Hours</th>
                  <th className={TH}>Recorded by</th>
                  <th className={TH}>Certificate</th>
                </tr>
              </thead>
              <tbody>
                {records.map((r) => (
                  <tr key={r.id} className="border-t border-v2border-muted">
                    <td className={`${TD} text-ink`}>{r.training_title}</td>
                    <td className={TD}>{formatDate(r.training_date)}</td>
                    <td className={TD}>{r.hours ?? '—'}</td>
                    <td className={TD}>{r.reported_by === 'self' ? 'Self' : 'Admin'}</td>
                    <td className={TD}>
                      {r.certificate ? (
                        r.certificate.filename
                      ) : r.certificate_missing ? (
                        <Pill tone="attention">Missing</Pill>
                      ) : (
                        <span className="text-ink-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}
